import React, { useMemo } from 'react';
import { Formik } from 'formik';
import * as yup from 'yup';
import MyAccountForm from './MyAccountForm';
import useFetchMyAccountDetails from './hooks/useFetchMyAccountDetails';
import FormButtonGroup from 'App/components/styles/FormButtonGroup';
import SubmitButton from 'Shared/form/SubmitButton';
import useUpdateMyAccountDetailsMutation from './hooks/useUpdateMyAccountDetailsMutation';
import Page from 'App/components/Page';
import { states } from 'App/components/PatientForm/constants';
import ResetButton from 'Shared/form/ResetButton';

const validationSchema = yup.object().shape({
    firstName: yup.string().required('Please enter a first name').nullable(),
    lastName: yup.string().required('Please enter a last name').nullable(),
    email: yup.string().email('Invalid email format').required('Please enter an email'),
    zipcode: yup.string().matches(/^\d{5}(?:-\d{4})?$/, 'The zip is not in the correct format'),
    telephone: yup.string().min(10, 'The phone number must be 10 digits'),
});

function MyAccount() {
    const { data, isLoading } = useFetchMyAccountDetails();
    const saveMyAccountForm = useUpdateMyAccountDetailsMutation();

    const initialValues = useMemo(() => {
        if (isLoading) {
            return null;
        }

        const timezone = data.timezones?.find((t) => {
            return Number(data.timeZoneId) ? t.timeZoneId === data.timeZoneId : t.timeZoneId === '13';
        });

        return {
            firstName: data.firstName,
            lastName: data.lastName,
            addressLine1: data.address_1_1,
            addressLine2: data.address_1_2,
            email: data.email,
            jobTitle: data.title,
            city: data.city_1,
            state: states.find((state) => state.value === data.province_1),
            telephone: data.telephone_1,
            zipcode: data.zipcode_1,
            country: data.country_1,
            ehrProviderId: data.ehrProviderId,
            timezone,
            daylightSavings: data.daylightSavings ? data.daylightSavings === '1' : false,
            messageEmail: data.messageEmail ? data.messageEmail === 'Y' : true,
            checkVerbose: data.verbose ? data.verbose === '1' : true,
        };
    }, [data, isLoading]);

    const timeZoneList = useMemo(() => {
        if (data) {
            return data.timezones;
        }
        return [];
    }, [data]);

    return (
        <Page title="My Account" isLoading={isLoading}>
            {!isLoading && (
                <div className="container page-wrapper">
                    <Formik
                        validationSchema={validationSchema}
                        initialValues={initialValues}
                        onSubmit={(values, { resetForm }) => {
                            const formData = new FormData();
                            formData.append('attributes[ehr_provider_id]', values.ehrProviderId);
                            formData.append('registerSubmit', 'true');
                            formData.append('user[address_1_1]', values.addressLine1);
                            formData.append('user[address_1_2]', values.addressLine2);
                            formData.append('user[city_1]', values.city);
                            formData.append('user[country_1]', values.country);
                            formData.append('user[daylight_savings]', values.daylightSavings ? '1' : '0');
                            formData.append('user[email]', values.email);
                            formData.append('user[first_name]', values.firstName);
                            formData.append('user[last_name]', values.lastName);
                            formData.append('user[message_email]', values.messageEmail ? 'Y' : 'N');
                            formData.append('user[province_1]', values.state?.value);
                            formData.append('user[telephone_1]', values.telephone);
                            formData.append('user[time_zone_id]', values.timezone?.timeZoneId || '13');
                            formData.append('user[title]', values.jobTitle);
                            formData.append('user[verbose]', values.checkVerbose ? '1' : '0');
                            formData.append('user[zipcode_1]', values.zipcode);

                            return saveMyAccountForm.mutateAsync(formData).then(() => resetForm({ values }));
                        }}
                    >
                        {({ handleSubmit }) => (
                            <form onSubmit={handleSubmit}>
                                <MyAccountForm timeZoneList={timeZoneList} />
                                <FormButtonGroup>
                                    <ResetButton>Reset</ResetButton>
                                    <SubmitButton>Save</SubmitButton>
                                </FormButtonGroup>
                            </form>
                        )}
                    </Formik>
                </div>
            )}
        </Page>
    );
}
export default MyAccount;
